import { match } from '@/utils/pattern-match-util'
import { type WorkspaceApplicantStatus } from './WorkspaceApplicant'
import {
  type WorkspacePostingOpportunityType,
  type WorkspacePostingStatus,
} from './WorkspacePosting'

export type WorkspacePostingStatusVariant =
  'Active' | 'Closed' | 'Draft' | 'InReview' | 'Unspecified'

export type WorkspaceApplicantStatusVariant =
  | 'Accepted'
  | 'Applied'
  | 'Invited'
  | 'Expired'
  | 'Rejected'
  | 'Withdrawn'
  | 'Unspecified'

/**
 * Formats the opportunity type of a workspace posting for display.
 */
export function formatOpportunityType(
  opportunityType: WorkspacePostingOpportunityType,
) {
  switch (opportunityType) {
    case 'Unknown':
      return 'Unknown'
    case 'Outsourcing':
      return 'Outsourcing'
    case 'SignAndFile':
      return 'Sign and File'
  }
}

/**
 * Formats an opportunity type option of the posting form, prompting for a
 * selection when none is made.
 */
export function formatOpportunityTypeOption(
  opportunityType: WorkspacePostingOpportunityType | undefined,
) {
  switch (opportunityType) {
    case 'SignAndFile':
      return 'Sign and File'
    case 'Outsourcing':
      return 'Outsourcing'
    case 'Unknown':
    case undefined:
      return 'Select an Opportunity Type'
  }
}

/**
 * Formats the status of a workspace posting for display.
 */
export function formatWorkspacePostingStatus(status: WorkspacePostingStatus) {
  return match(status, {
    Active: () => 'Active',
    Cancelled: () => 'Cancelled',
    Closed: () => 'Completed',
    Draft: () => 'Draft',
    InReview: () => 'In review',
    Null: () => 'Unspecified',
  })
}

/**
 * Maps the status of a workspace posting to the variant it is shown with.
 */
export function workspacePostingStatusVariant(
  status: WorkspacePostingStatus,
): WorkspacePostingStatusVariant {
  return match<WorkspacePostingStatus, WorkspacePostingStatusVariant>(status, {
    Active: () => 'Active',
    Cancelled: () => 'Closed',
    Closed: () => 'Closed',
    Draft: () => 'Draft',
    InReview: () => 'InReview',
    Null: () => 'Unspecified',
  })
}

/**
 * Formats the status of a workspace applicant for display.
 */
export function formatWorkspaceApplicantStatus(
  status: WorkspaceApplicantStatus,
) {
  return match(status, {
    Accepted: () => 'Accepted',
    Applied: () => 'Applied',
    Invited: () => 'Invited',
    Expired: () => 'Expired',
    Rejected: () => 'Rejected',
    Withdrawn: () => 'Withdrawn',
    Null: () => 'Unspecified',
  })
}

/**
 * Formats the status of a workspace applicant from the point of view of the
 * admin managing the applicants.
 */
export function formatManagedWorkspaceApplicantStatus(
  status: WorkspaceApplicantStatus,
) {
  return match(status, {
    Accepted: () => 'Joined',
    Applied: () => 'Applied',
    Invited: () => 'Invited',
    Expired: () => 'Expired',
    Rejected: () => 'Declined',
    Withdrawn: () => 'Withdrawn',
    Null: () => 'Unspecified',
  })
}

/**
 * Maps the status of a workspace applicant to the variant it is shown with.
 */
export function workspaceApplicantStatusVariant(
  status: WorkspaceApplicantStatus,
): WorkspaceApplicantStatusVariant {
  return match<WorkspaceApplicantStatus, WorkspaceApplicantStatusVariant>(
    status,
    {
      Accepted: () => 'Accepted',
      Applied: () => 'Applied',
      Invited: () => 'Invited',
      Expired: () => 'Expired',
      Rejected: () => 'Rejected',
      Withdrawn: () => 'Withdrawn',
      Null: () => 'Unspecified',
    },
  )
}
